import { useState } from 'react';
import { formatFieldValue, isEmptyValue, type FieldValue, type RowKind } from '../../lib/fieldValue';
import {
  allowedRowKinds,
  canEdit,
  cellKey,
  type FieldCell,
  type LockEntry,
  type Reveal,
  type RoomRole,
  type RoomStatus,
} from '../../lib/rooms';
import { fieldsBySection, overlayGroups, type Field, type Template } from '../../lib/templates';
import { imageUrl } from '../../lib/whiteboard';
import type { Presence, PresenceFocus } from '../../store/presenceStore';
import { FieldEditor } from './FieldEditor';
import { LockBadge } from './LockBadge';

interface RatingTableProps {
  roomId: number;
  template: Template;
  guestLabel: string;
  myRole: RoomRole;
  myUserId: number;
  status: RoomStatus;
  cells: Map<string, FieldCell>;
  locks: LockEntry[];
  activeReveal: Reveal | null;
  presence: Presence[];
  onCommit: (fieldId: number, rowKind: RowKind, value: FieldValue | null) => void;
  onToggleLock: (targetId: number, locked: boolean) => void;
  onReveal: (fieldId: number, rowKind: RowKind) => void;
  onHideReveal: () => void;
  onFocusChange: (focus: PresenceFocus | null) => void;
}

interface OpenCell {
  field: Field;
  rowKind: RowKind;
}

const ROWS: { rowKind: RowKind; labelFor: (guestLabel: string) => string }[] = [
  { rowKind: 'PLAYER', labelFor: (guestLabel) => guestLabel },
  { rowKind: 'COMMUNITY', labelFor: () => 'Community' },
];

function isFieldLocked(locks: LockEntry[], fieldId: number): boolean {
  return locks.some((lock) => lock.targetKind === 'FIELD' && lock.targetId === fieldId);
}

function isSectionLocked(locks: LockEntry[], sectionId: number): boolean {
  return locks.some((lock) => lock.targetKind === 'SECTION' && lock.targetId === sectionId);
}

/** Someone else's presence whose focus is on this exact cell, or `null`. Excludes the current
 * user — a cell you have open yourself doesn't need a chip announcing that to you. */
function editorOf(presence: Presence[], myUserId: number, fieldId: number, rowKind: RowKind): Presence | null {
  return (
    presence.find(
      (person) =>
        person.userId !== myUserId && person.focus?.fieldId === fieldId && person.focus.rowKind === rowKind,
    ) ?? null
  );
}

/** The small lime "<name> editing" eyebrow DESIGN.md specifies for a cell someone else has open. */
function EditingChip({ username }: { username: string }) {
  return <span className="eyebrow block truncate text-accent">{username} editing</span>;
}

function tierColorVar(tier: string): string {
  const known = new Set(['sss', 's', 'a', 'b', 'c', 'd']);
  const key = tier.toLowerCase();
  return known.has(key) ? `var(--tier-${key})` : 'var(--text)';
}

function CellValue({ roomId, field, value }: { roomId: number; field: Field; value: FieldValue | null }) {
  if (value === null || isEmptyValue(value)) {
    return <span className="telemetry text-text-faint">—</span>;
  }
  if (value.type === 'TIER') {
    return (
      <span className="display text-3xl leading-none" style={{ color: tierColorVar(value.tier) }}>
        {value.tier}
        {value.footnote ? ' *' : ''}
      </span>
    );
  }
  if (value.type === 'IMAGE') {
    return (
      <img
        src={imageUrl(roomId, value.imageAssetId)}
        alt={value.alt ?? ''}
        className="h-9 w-9 rounded-sm border border-border-strong object-cover"
      />
    );
  }
  return <span className="line-clamp-2 text-sm text-text">{formatFieldValue(value, field.config)}</span>;
}

/**
 * Rows are scoreboard rows: `Player`/`Community` (per DESIGN.md), columns grouped under their
 * `overlayGroup` eyebrow. A field whose `appliesTo` doesn't cover a row (e.g. PLAYER_ONLY in the
 * Community row) renders a diagonal-stripe placeholder cell instead of a button. `appliesTo: ROOM`
 * fields never appear here — they render below as the Notes panel, grouped by section.
 */
export function RatingTable({
  roomId,
  template,
  guestLabel,
  myRole,
  myUserId,
  status,
  cells,
  locks,
  activeReveal,
  presence,
  onCommit,
  onToggleLock,
  onReveal,
  onHideReveal,
  onFocusChange,
}: RatingTableProps) {
  const [openCell, setOpenCell] = useState<OpenCell | null>(null);

  const groups = overlayGroups(template);
  const fieldsByGroup = new Map<string, Field[]>();
  for (const group of groups) {
    fieldsByGroup.set(
      group,
      template.fields.filter((field) => field.overlayGroup === group).sort((a, b) => a.sortOrder - b.sortOrder),
    );
  }
  const roomSections = fieldsBySection(template)
    .map((entry) => ({ section: entry.section, fields: entry.fields.filter((field) => field.appliesTo === 'ROOM') }))
    .filter((entry) => entry.fields.length > 0);

  const isHostOrCohost = myRole === 'HOST' || myRole === 'COHOST';

  function openIfEditable(field: Field, rowKind: RowKind) {
    setOpenCell({ field, rowKind });
  }

  // Cell buttons stay enabled — and therefore focusable/openable — for everyone who can view the
  // room, even when they can't edit the cell: a disabled button drops out of the tab order, which
  // would make a locked or read-only cell unreachable by keyboard. `editable` still gets passed
  // through to `FieldEditor` as `disabled`, which is what actually renders the read-only branch
  // (the value plus a "Read-only" eyebrow, no controls) instead of the type-specific editor.
  function renderCellButton(field: Field, rowKind: RowKind) {
    const key = cellKey(field.id, rowKind);
    const cell = cells.get(key);
    const locked = isFieldLocked(locks, field.id) || isSectionLocked(locks, field.sectionId);
    const editable = canEdit(myRole, field, rowKind, locks, status);
    const isReveal = activeReveal !== null && activeReveal.fieldId === field.id && activeReveal.rowKind === rowKind;
    const editor = editorOf(presence, myUserId, field.id, rowKind);

    return (
      <button
        type="button"
        aria-label={`${field.label} · ${rowKind === 'PLAYER' ? guestLabel : 'Community'}`}
        onClick={() => openIfEditable(field, rowKind)}
        className={[
          'flex min-h-tap w-full flex-col items-start gap-1 rounded-md border px-3 py-2 text-left transition-colors',
          editable ? '' : 'opacity-70',
          isReveal ? 'border-signal' : locked ? 'border-border-strong' : 'border-transparent hover:border-border-strong',
        ].join(' ')}
      >
        {locked && <LockBadge />}
        {editor && <EditingChip username={editor.username} />}
        <CellValue roomId={roomId} field={field} value={cell?.value ?? null} />
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      {groups.map((group, groupIndex) => {
        const fields = fieldsByGroup.get(group) ?? [];
        return (
          <section key={group} className="reveal" style={{ ['--i' as string]: groupIndex }} aria-label={group}>
            <div className="flex items-baseline gap-4">
              <span className="eyebrow">
                {String(groupIndex + 1).padStart(2, '0')} / {group}
              </span>
              <span className="h-px flex-1 bg-border" aria-hidden="true" />
            </div>

            {/* Desktop: a real table, columns = fields, rows = Player/Community. */}
            <div className="mt-3 hidden overflow-x-auto md:block">
              <table className="w-full min-w-max border-collapse">
                <thead>
                  <tr>
                    <th className="w-32 border-b border-border pb-2 pr-3 text-left align-bottom">
                      <span className="sr-only">Row</span>
                    </th>
                    {fields.map((field) => (
                      <th
                        key={field.id}
                        scope="col"
                        className="min-w-[7rem] border-b border-border px-3 pb-2 text-left align-bottom text-sm font-semibold"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(({ rowKind, labelFor }) => (
                    <tr key={rowKind} className="border-b border-border/60">
                      <th scope="row" className="whitespace-nowrap py-3 pr-3 text-left text-sm font-semibold text-text-muted">
                        {labelFor(guestLabel)}
                      </th>
                      {fields.map((field) => {
                        const applicable = allowedRowKinds(field).includes(rowKind);
                        return (
                          <td key={field.id} data-testid={`cell-${field.id}-${rowKind}`} className="min-w-[7rem] px-1 py-1 align-top">
                            {applicable ? renderCellButton(field, rowKind) : <div className="cell-stripe h-full min-h-tap rounded-md" aria-hidden="true" />}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: a per-field list (label, then Player and Community cells). */}
            <div className="mt-3 flex flex-col gap-4 md:hidden">
              {fields.map((field) => {
                const applies = allowedRowKinds(field);
                return (
                  <div key={field.id} className="rounded-md border border-border px-3 py-3">
                    <span className="eyebrow block">{field.label}</span>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {ROWS.map(({ rowKind, labelFor }) => (
                        <div key={rowKind} data-testid={`cell-${field.id}-${rowKind}`}>
                          <span className="block text-xs text-text-faint">{labelFor(guestLabel)}</span>
                          {applies.includes(rowKind) ? (
                            renderCellButton(field, rowKind)
                          ) : (
                            <div className="cell-stripe mt-1 h-11 rounded-md" aria-hidden="true" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {roomSections.length > 0 && (
        <section aria-label="Notes" className="reveal">
          <div className="flex items-baseline gap-4">
            <span className="eyebrow">Notes</span>
            <span className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>
          <div className="mt-4 flex flex-col gap-6">
            {roomSections.map(({ section, fields }) => (
              <div key={section.id}>
                <span className="eyebrow">{section.name}</span>
                <div className="mt-2 flex flex-col gap-2">
                  {fields.map((field) => {
                    const cell = cells.get(cellKey(field.id, 'ROOM'));
                    const editable = canEdit(myRole, field, 'ROOM', locks, status);
                    const editor = editorOf(presence, myUserId, field.id, 'ROOM');
                    return (
                      <button
                        key={field.id}
                        type="button"
                        onClick={() => openIfEditable(field, 'ROOM')}
                        className={[
                          'min-h-tap rounded-md border border-border-strong px-3 py-2 text-left hover:border-accent-border',
                          editable ? '' : 'opacity-70',
                        ].join(' ')}
                      >
                        <span className="eyebrow block">{field.label}</span>
                        {editor && <EditingChip username={editor.username} />}
                        <CellValue roomId={roomId} field={field} value={cell?.value ?? null} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {openCell && (
        <FieldEditor
          roomId={roomId}
          field={openCell.field}
          rowKind={openCell.rowKind}
          rowLabel={openCell.rowKind === 'PLAYER' ? guestLabel : openCell.rowKind === 'COMMUNITY' ? 'Community' : 'Room note'}
          cell={cells.get(cellKey(openCell.field.id, openCell.rowKind))}
          disabled={!canEdit(myRole, openCell.field, openCell.rowKind, locks, status)}
          isLocked={isFieldLocked(locks, openCell.field.id)}
          canToggleLock={isHostOrCohost}
          isActiveReveal={
            activeReveal !== null && activeReveal.fieldId === openCell.field.id && activeReveal.rowKind === openCell.rowKind
          }
          canReveal={isHostOrCohost && openCell.rowKind !== 'ROOM'}
          onCommit={(value) => onCommit(openCell.field.id, openCell.rowKind, value)}
          onToggleLock={() => onToggleLock(openCell.field.id, !isFieldLocked(locks, openCell.field.id))}
          onReveal={() => onReveal(openCell.field.id, openCell.rowKind)}
          onHideReveal={onHideReveal}
          onFocusChange={onFocusChange}
          onClose={() => setOpenCell(null)}
        />
      )}
    </div>
  );
}
